import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, TrendingDown, TrendingUp, Award, Medal, ArrowLeft } from 'lucide-react';
import { ClubhouseNav } from '../components/clubhouse/ClubhouseNav';
import { clubhouseApi, logoutClubhouse } from '../utils/clubhouseApi';
import './ClubhouseLeaderboards.css';



export const ClubhouseLeaderboards = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('monthly');
  const [monthlyLeaderboard, setMonthlyLeaderboard] = useState(null);
  const [quarterlyLeaderboard, setQuarterlyLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem('clubhouse_user')) {
      navigate('/clubhouse/login');
      return;
    }

    fetchLeaderboards();
    fetchUserProfile();
  }, [navigate]);

  const fetchUserProfile = async () => {
    try {
      const response = await clubhouseApi.get(`/api/clubhouse/profile/me`);
      setUserProfile(response.data);
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    }
  };

  const fetchLeaderboards = async () => {
    try {
      const [monthlyRes, quarterlyRes] = await Promise.all([
        clubhouseApi.get(`/api/clubhouse/handicap/leaderboard/monthly-lowest`),
        clubhouseApi.get(`/api/clubhouse/handicap/leaderboard/quarterly-most-improved`)
      ]);

      setMonthlyLeaderboard(monthlyRes.data);
      setQuarterlyLeaderboard(quarterlyRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        await logoutClubhouse();
        navigate('/clubhouse/login');
      } else {
        setError('Failed to load leaderboards');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logoutClubhouse();
    navigate('/clubhouse/login');
  };

  const getMedalIcon = (rank) => {
    if (rank === 1) return <Medal className="medal gold" />;
    if (rank === 2) return <Medal className="medal silver" />;
    if (rank === 3) return <Medal className="medal bronze" />;
    return <span className="rank-number">#{rank}</span>;
  };

  return (
    <div className="clubhouse-leaderboards">
      {/* Navigation */}
      <ClubhouseNav onLogout={handleLogout} userProfile={userProfile} />

      {/* Header */}
      <header className="leaderboards-header">
        <div className="container">
          <div className="header-content">
            <div className="header-title">
              <h1>Leaderboards</h1>
              <p>See who's dominating the fairways this month and quarter</p>
            </div>
          </div>
        </div>
      </header>

      <div className="container leaderboards-container">
        {error && (
          <div className="error-banner">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="leaderboard-tabs">
          <button 
            className={`tab-btn ${activeTab === 'monthly' ? 'active' : ''}`}
            onClick={() => setActiveTab('monthly')}
          >
            <TrendingDown size={20} />
            Monthly Lowest
          </button>
          <button 
            className={`tab-btn ${activeTab === 'quarterly' ? 'active' : ''}`}
            onClick={() => setActiveTab('quarterly')}
          >
            <TrendingUp size={20} />
            Quarterly Most Improved
          </button>
        </div>

        {/* Monthly Leaderboard */}
        {activeTab === 'monthly' && monthlyLeaderboard && (
          <div className="leaderboard-content">
            <div className="leaderboard-header">
              <h2>Monthly Lowest Handicap</h2>
              <p className="period-label">{formatMonth(monthlyLeaderboard.month)}</p>
            </div>

            {monthlyLeaderboard.entries.length === 0 ? (
              <div className="empty-leaderboard">
                <Award size={48} />
                <p>No entries yet this month</p>
                <small>Be the first to add your handicap and compete!</small>
              </div>
            ) : (
              <div className="leaderboard-table">
                {monthlyLeaderboard.entries.map((entry) => (
                  <div key={entry.user_id} className={`leaderboard-entry rank-${entry.rank}`}>
                    <div className="entry-rank">
                      {getMedalIcon(entry.rank)}
                    </div>
                    <div className="entry-photo">
                      {entry.profile_photo_url ? (
                        <img src={entry.profile_photo_url} alt={entry.first_name} />
                      ) : (
                        <div className="photo-placeholder">
                          {(entry.first_name || entry.email)?.[0]?.toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="entry-info">
                      <div className="entry-name">{entry.first_name || entry.email.split('@')[0]}</div>
                      <div className="entry-email">{entry.email}</div>
                    </div>
                    <div className="entry-score">
                      <div className="score-value">{entry.handicap_index.toFixed(1)}</div>
                      <div className="score-label">Index</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Quarterly Leaderboard */}
        {activeTab === 'quarterly' && quarterlyLeaderboard && (
          <div className="leaderboard-content">
            <div className="leaderboard-header">
              <h2>Quarterly Most Improved</h2>
              <p className="period-label">{formatQuarter(quarterlyLeaderboard.quarter)}</p>
            </div>

            {quarterlyLeaderboard.entries.length === 0 ? (
              <div className="empty-leaderboard">
                <TrendingUp size={48} />
                <p>No improvements yet this quarter</p>
                <small>Track your handicap over time to see your improvement!</small>
              </div>
            ) : (
              <div className="leaderboard-table">
                {quarterlyLeaderboard.entries.map((entry) => (
                  <div key={entry.user_id} className={`leaderboard-entry rank-${entry.rank}`}>
                    <div className="entry-rank">
                      {getMedalIcon(entry.rank)}
                    </div>
                    <div className="entry-photo">
                      {entry.profile_photo_url ? (
                        <img src={entry.profile_photo_url} alt={entry.first_name} />
                      ) : (
                        <div className="photo-placeholder">
                          {(entry.first_name || entry.email)?.[0]?.toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="entry-info">
                      <div className="entry-name">{entry.first_name || entry.email.split('@')[0]}</div>
                      <div className="entry-email">{entry.email}</div>
                    </div>
                    <div className="entry-score">
                      <div className="score-value improvement">-{entry.improvement.toFixed(1)}</div>
                      <div className="score-label">Improved</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const formatMonth = (monthStr) => {
  const [year, month] = monthStr.split('-');
  const date = new Date(year, parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const formatQuarter = (quarterStr) => {
  const [year, quarter] = quarterStr.split('-');
  return `${quarter.replace('Q', 'Quarter ')} ${year}`;
};